/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { CustomAppState, xHeaderActions } from "@eisinc/App/Store/Reducers";
import { screen } from "@eisinc/XDL/Helpers";
import { color } from "@eisinc/XDL/Utilities";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Page, { PageProps as ContainerProps } from "@insite/mobius/Page";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, DispatchProp } from "react-redux";
import { css } from "styled-components";

export interface LogoLinksSearchStyles {
    header: InjectableCss;
    placeholder: InjectableCss;
    container: ContainerProps;
    logoWrapper: InjectableCss;
    searchMenuWrapper: InjectableCss;
    ctaWrapper: InjectableCss;
    quickOrderWrapper?: InjectableCss;
    rfqWrapper?: InjectableCss;
    salesRepWrapper?: InjectableCss;
}

export const HeaderSize: any = {
    default: 145,
    sticky: 75,
    mobile: 120,
    top: 30,

    full: 230,
    nav: 50,
    gsm: 50,
};

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    header: {
        css: css`
            height: ${HeaderSize.default}px;
            box-shadow: 0 5px 10px -10px rgba(0, 0, 0, 0.2);
            position: absolute;
            top: ${HeaderSize.top}px;
            width: 100%;
            background: white;
            z-index: 10;

            ${screen("lte", "sm")`
                position: fixed;
                top:0;
                height: ${HeaderSize.mobile}px;

                & > div {
                    overflow: visible !important;
                }
            `}
            ${({ globalMessage }: any) =>
                globalMessage &&
                css`
                    top: ${HeaderSize.top + HeaderSize.gsm}px;
                    ${screen("lte", "sm")`top:${HeaderSize.gsm}px`}
                `}
            ${({ out }: any) =>
                out &&
                `
                    top: -${HeaderSize.default}px;
                    transition: top 400ms ease-out;
                `}
            ${({ sticky }: any) =>
                sticky &&
                css`
                    position: fixed;
                    top: 0;
                    height: ${HeaderSize.sticky}px;
                    background: ${color("common.ui3")};
                    box-shadow: 0 5px 10px -10px rgba(0, 0, 0, 0.9);
                `}
        `,
    },
    placeholder: {
        css: css`
            height: ${HeaderSize.default}px;
            ${screen("lte", "sm")`
                height: ${HeaderSize.mobile}px;
            `}
            ${({ sticky }: any) => sticky && `height: ${HeaderSize.default}px;`}
            ${({ expandHeight }: any) => expandHeight && `height: ${HeaderSize.default + 150}px;`}
        `,
    },
    container: {
        as: "div",
        fullWidth: [true, true, false, false, false],
        padding: 0,
        background: "transparent",
        css: css`
            display: flex;
            justify-content: space-between;
            height: 100%;
            ${screen("lte", "sm")`flex-wrap: wrap;`};
            & > div {
                display: flex;
                flex-direction: column;
                justify-content: center;
                // border: 1px solid black;
            }
        `,
    },
    logoWrapper: {
        css: css`
            text-align: left;
            padding: 0 45px 0 10px;
            ${screen("lte", "sm")`
                max-width: 45%;
            `}
            ${screen("lte", "md")`flex: auto;`}
            ${({ sticky }: any) => sticky && `padding: 0 45px 0 10px;`}
            img {
                height: 78px;
                width: auto;
                ${screen("lte", "md")`
                    height: 42px;
                    width: auto;
                    margin: 0;
                  `}
                ${({ sticky }: any) => sticky && `height:54px;width: auto;`}
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            text-align: center;
            padding: 0 10px 0;
            margin-bottom: 4px;
            flex: auto;
            ${screen("lte", "sm")`order:1;width: 100%;`}
            ${({ sticky }: any) => sticky && `padding: 0 25px 0;`}
        `,
    },
    ctaWrapper: {
        css: css`
            transition: all 400ms ease-out;
            padding: 0 25px 0;
            &:last-child {
                padding-right: 10px;
            }
            ${screen("lte", "md")`max-width: 20%;padding: 0 15px 0;`}
            ${screen("lte", "sm")`
                max-width: 20%;padding: 0 10px 0;
                &:last-child {
                    padding-right: 25px;
                }
            `}
            ${screen("lte", "xs")`
                max-width: 20%;
                padding: 0 5px 0;
                
                &:last-child {
                    padding-right: 10px;
                }
            `}
            ${({ sticky }: any) => sticky && `padding: 0 10px 0;`}
        `,
    },
};

logoLinksSearchStyles.quickOrderWrapper = {
    css: css`
        ${logoLinksSearchStyles.ctaWrapper.css}
        ${({ sticky, canAddWidget }: any) => !sticky && !canAddWidget && ``}
    `,
};

logoLinksSearchStyles.rfqWrapper = {
    css: css`
        ${logoLinksSearchStyles.ctaWrapper.css}
        ${({ sticky, canAddWidget }: any) => !sticky && !canAddWidget && ``}
    `,
};

logoLinksSearchStyles.salesRepWrapper = {
    css: css`
        ${logoLinksSearchStyles.ctaWrapper.css}
        ${({ isAuthenticated, canAddWidget }: any) => !isAuthenticated && !canAddWidget && `display: none !important;`}
    `,
};

const mapStateToProps = (state: CustomAppState) => ({
    isSticky: state.xHeader.isSticky,
    stickyMenu: state.xHeader.stickyMenu,
    globalMessage: state.xHeader.globalMessage,
    permissions: state.context.permissions,
    pageType: getCurrentPage(state).type,
    session: state.context.session,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & DispatchProp<xHeaderActions>;

const StyledWrapperX = getStyledWrapper("div") as any; // typeof StyledWrapper & { sticky: boolean };
const isMobile = () => window.matchMedia("(max-width: 768px) and (hover: none)").matches;
const isTablet = () => window.matchMedia("(max-width: 1024px) and (hover: none)").matches;

const LogoLinksSearch: FC<Props> = ({
    id,
    dispatch,
    isSticky,
    stickyMenu,
    globalMessage,
    permissions,
    pageType,
    session,
}) => {
    const placeholder = React.createRef<HTMLUListElement>();
    const [isHeaderOut, setHeaderOut] = useState(false);

    const handleSticky = () => {
        const p: any = placeholder?.current?.getBoundingClientRect();
        const mobile = isMobile();
        // const tablet = isTablet();
        const top = window.scrollY;

        const hSize = p?.top + top + p?.height; // + (!tablet ? HeaderSize.nav : 0);
        const newValue = top > hSize && !mobile;
        if (newValue !== isSticky) {
            dispatch({ type: "xHeader/isSticky", isSticky: newValue });
            if (stickyMenu) {
                dispatch({ type: "xHeader/stickyMenu", stickyMenu: false });
            }
        }

        setHeaderOut(top > HeaderSize.default + (globalMessage ? HeaderSize.gsm : 0) && !mobile);
    };

    useEffect(() => {
        document.addEventListener("scroll", handleSticky, { passive: true });
        window.addEventListener("resize", handleSticky);
        handleSticky();

        return () => {
            document.removeEventListener("scroll", handleSticky);
            window.removeEventListener("resize", handleSticky);
        };
    });

    return (
        <>
            <StyledWrapperX
                ref={placeholder}
                sticky={isSticky}
                globalMessage={globalMessage}
                expandHeight={permissions?.canAddWidget && pageType === "Header"}
                {...logoLinksSearchStyles.placeholder}
            />
            <StyledWrapperX
                sticky={isSticky}
                out={isHeaderOut}
                globalMessage={globalMessage}
                {...logoLinksSearchStyles.header}
            >
                <Page {...logoLinksSearchStyles.container}>
                    <StyledWrapperX sticky={isSticky} {...logoLinksSearchStyles.logoWrapper}>
                        <Zone contentId={id} zoneName="Logo" />
                    </StyledWrapperX>
                    <StyledWrapperX sticky={isSticky} {...logoLinksSearchStyles.searchMenuWrapper}>
                        <Zone contentId={id} zoneName="Search" />
                    </StyledWrapperX>
                    <StyledWrapperX
                        sticky={isSticky}
                        canAddWidget={permissions?.canAddWidget}
                        {...logoLinksSearchStyles.quickOrderWrapper}
                    >
                        <Zone contentId={id} zoneName="QuickOrder" />
                    </StyledWrapperX>
                    <StyledWrapperX
                        sticky={isSticky}
                        canAddWidget={permissions?.canAddWidget}
                        {...logoLinksSearchStyles.rfqWrapper}
                    >
                        <Zone contentId={id} zoneName="RFQ" />
                    </StyledWrapperX>
                    <StyledWrapperX
                        sticky={isSticky}
                        canAddWidget={permissions?.canAddWidget}
                        isAuthenticated={session.isAuthenticated}
                        {...logoLinksSearchStyles.salesRepWrapper}
                    >
                        <Zone contentId={id} zoneName="SalesRep" />
                    </StyledWrapperX>
                    <StyledWrapperX sticky={isSticky} {...logoLinksSearchStyles.ctaWrapper}>
                        <Zone contentId={id} zoneName="SignIn" />
                    </StyledWrapperX>
                    <StyledWrapperX sticky={isSticky} {...logoLinksSearchStyles.ctaWrapper}>
                        <Zone contentId={id} zoneName="Cart" />
                    </StyledWrapperX>
                </Page>
            </StyledWrapperX>
        </>
    );
};

const logoLinksSearch: WidgetModule = {
    component: connect(mapStateToProps)(LogoLinksSearch),
    definition: {
        group: "Common",
        icon: "Filter",
    },
};

export default logoLinksSearch;
